import React from 'react';
import styled, { keyframes } from 'styled-components';
import ScrollFadeIn from '../components/ScrollFadeIn';
import { Boxes, Puzzle, BarChart3, Smartphone, Check } from 'lucide-react';

const VTAPage: React.FC = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroIcon><Boxes /></HeroIcon>
          <HeroTitle>VTA</HeroTitle>
          <HeroTagline>Value Type Assessment (가치관 유형 검사)</HeroTagline>
          <HeroDescription>
            사용자의 응답 패턴을 분석하여 개인의 핵심 가치관을 진단하는 React 기반의 심리 테스트 웹 애플리케이션입니다.
          </HeroDescription>
        </HeroContent>
      </HeroSection>

      <FeaturesSection>
        <SectionTitle>주요 특징</SectionTitle>
        <FeaturesGrid>
          <ScrollFadeIn direction="up" delay={0.1}>
            <FeatureCard>
              <FeatureIcon><Puzzle /></FeatureIcon>
              <FeatureTitle>모듈형 설문 시스템</FeatureTitle>
              <FeatureDescription>
                쉽게 질문과 문항을 구성할 수 있는 데이터 구조로 설계되어, 코드를 복잡하게 섞지 않고도 손쉽게 새로운 테스트를 제작할 수 있습니다.
              </FeatureDescription>
            </FeatureCard>
          </ScrollFadeIn>
          <ScrollFadeIn direction="up" delay={0.2}>
            <FeatureCard>
              <FeatureIcon><Smartphone /></FeatureIcon>
              <FeatureTitle>반응형 UI & 매끄러운 UX</FeatureTitle>
              <FeatureDescription>
                스마트폰부터 태블릿, 데스크톱까지 완벽하게 지원되는 크로스 플랫폼 호환성과 직관적인 애플리케이션 화면 전환을 제공합니다.
              </FeatureDescription>
            </FeatureCard>
          </ScrollFadeIn>
          <ScrollFadeIn direction="up" delay={0.3}>
            <FeatureCard>
              <FeatureIcon><BarChart3 /></FeatureIcon>
              <FeatureTitle>시각적 결과 리포트</FeatureTitle>
              <FeatureDescription>
                단순히 글자로 결과를 알려주는 것이 아닌, 타입별 고유한 테마 색상과 이미지를 활용하여 검사 결과를 직관적으로 공유하도록 구성되어 있습니다.
              </FeatureDescription>
            </FeatureCard>
          </ScrollFadeIn>
        </FeaturesGrid>
      </FeaturesSection>

      <BenefitsSection>
        <BenefitsContent>
          <BenefitsText>
            <SectionTitle>VTA 테스트의 장점</SectionTitle>
            <BenefitsList>
              <ScrollFadeIn direction="up" delay={0.1}>
                <BenefitItem>
                  <BenefitIcon><Check /></BenefitIcon>
                  <BenefitText>
                    <BenefitTitle>빠르고 가벼운 성능</BenefitTitle>
                    <BenefitDescription>
                      Client-side에서 모든 검사 로직이 동작하므로 지연시간 없이 즉각적인 문항 전환이 이루어집니다.
                    </BenefitDescription>
                  </BenefitText>
                </BenefitItem>
              </ScrollFadeIn>
              <ScrollFadeIn direction="up" delay={0.2}>
                <BenefitItem>
                  <BenefitIcon><Check /></BenefitIcon>
                  <BenefitText>
                    <BenefitTitle>데이터 커스터마이징</BenefitTitle>
                    <BenefitDescription>
                      앱 내부의 상수 파일 구조화가 잘 되어 있어, 가치관 검사 외에도 심리테스트 등 완전히 다른 목적의 검사로도 코드 변경 없이 활용이 가능합니다.
                    </BenefitDescription>
                  </BenefitText>
                </BenefitItem>
              </ScrollFadeIn>
            </BenefitsList>
          </BenefitsText>
        </BenefitsContent>
      </BenefitsSection>

      <CTASection>
        <CTAContent>
          <CTATitle>테스트 로직 살펴보기</CTATitle>
          <CTADescription>
            VTA의 심도 있는 진단 시스템을 지금 바로 경험해보세요.
          </CTADescription>
          <CTAButtons>
            <PrimaryButton href="https://vta.freakit.co.kr/" target="_blank" rel="noopener noreferrer">
              테스트 바로가기
            </PrimaryButton>
          </CTAButtons>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
};

export default VTAPage;

/* ────────── Styled Components ────────── */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const PageContainer = styled.div`
  padding-top: 80px;
`;
const HeroSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  animation: ${fadeIn} 1s ease-out;
`;
const HeroIcon = styled.div`
  font-size: 3.25rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.accent};
`;
const HeroTitle = styled.h1`
  font-size: 2.6rem;
  font-weight: 800;
  margin-bottom: 1rem;
`;
const HeroTagline = styled.p`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 2rem;
  opacity: 0.95;
`;
const HeroDescription = styled.p`
  font-size: 1.25rem;
  line-height: 1.8;
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2.1rem;
  font-weight: 800;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 2rem;
`;

const FeaturesSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;
const FeaturesGrid = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(350px, 100%), 1fr));
  gap: 2rem;
`;
const FeatureCard = styled.div`
  padding: 1.75rem;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: ${({ theme }) => theme.transitions.cubic};
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;
const FeatureIcon = styled.div`
  font-size: 2.1rem;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: 1.5rem;
`;
const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;
const FeatureDescription = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const BenefitsSection = styled.section`
  padding: 4rem 2rem;
  background: transparent;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;
const BenefitsContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;
const BenefitsText = styled.div``;
const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
`;
const BenefitItem = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;
const BenefitIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 0.25rem;
`;
const BenefitText = styled.div`
  flex: 1;
`;
const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;
const BenefitDescription = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CTASection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;
const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const CTATitle = styled.h2`
  font-size: 2.1rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
`;
const CTADescription = styled.p`
  font-size: 1.5rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
`;
const CTAButtons = styled.div`
  display: flex;
  justify-content: center;
`;
const PrimaryButton = styled.a`
  display: inline-block;
  padding: 0.9rem 2rem;
  background: #ffffff;
  color: #020617;
  border-radius: 8px;
  font-weight: 700;
  font-size: 1.2rem;
  border: none;
  transition: ${({ theme }) => theme.transitions.cubic};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  }
`;
